import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getBlogIndex } from "./blogData";
import { homepageCopy } from "../content/homepageCopy";
import { useDocumentMeta } from "../seo/useDocumentMeta";

export default function BlogIndexPage() {
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState("loading");
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get("tag") || "";
  useDocumentMeta({
    title: homepageCopy.pages.blog.title,
    description: homepageCopy.pages.blog.description,
    path: "/blog",
  });
  const allTags = getRankedTags(posts);
  const topTags = allTags.slice(0, 8);
  const topTagSet = new Set(topTags);
  const visibleTopTags =
    activeTag && !topTagSet.has(activeTag) ? [...topTags, activeTag] : topTags;

  useEffect(() => {
    let isMounted = true;

    getBlogIndex()
      .then((allPosts) => {
        if (!isMounted) return;
        setPosts(allPosts);
        setStatus("ready");
      })
      .catch(() => {
        if (!isMounted) return;
        setStatus("error");
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <main className="main" id="top">
      <section className="section section--legal" aria-label="Blog posts">
        <div className="container blog blog--index">
          <h1 className="blog__title">Blog</h1>
          <p className="blog__intro">
            Product insights, company updates, and perspectives from the Loopora
            team.
          </p>

          {status === "loading" && <p>Loading posts...</p>}
          {status === "error" && (
            <p>We could not load blog posts. Please try again shortly.</p>
          )}
          {status === "ready" && posts.length === 0 && (
            <p>No published posts yet. Check back soon.</p>
          )}

          {status === "ready" && posts.length > 0 && (
            <>
              <div className="blog-tags">
                <button
                  type="button"
                  className={`blog-tag ${activeTag ? "" : "is-active"}`}
                  onClick={() => {
                    setSearchParams({});
                    setShowAllFilters(false);
                  }}
                >
                  All posts
                </button>
                {visibleTopTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className={`blog-tag ${activeTag === tag ? "is-active" : ""}`}
                    onClick={() => {
                      setSearchParams({ tag });
                      setShowAllFilters(false);
                    }}
                  >
                    {tag}
                  </button>
                ))}
                {allTags.length > 8 && (
                  <button
                    type="button"
                    className={`blog-tag ${showAllFilters ? "is-active" : ""}`}
                    onClick={() => setShowAllFilters((open) => !open)}
                    aria-expanded={showAllFilters}
                    aria-controls="more-filters-panel"
                  >
                    More filters
                  </button>
                )}
              </div>
              {showAllFilters && allTags.length > 8 && (
                <div className="blog-tags-panel" id="more-filters-panel">
                  {allTags.map((tag) => (
                    <button
                      key={`all-${tag}`}
                      type="button"
                      className={`blog-tag ${activeTag === tag ? "is-active" : ""}`}
                      onClick={() => {
                        setSearchParams({ tag });
                        setShowAllFilters(false);
                      }}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              )}

              <div className="blog__grid">
                {getVisiblePosts(posts, activeTag).map((post) => (
                  <article className="blog-card card" key={post.slug}>
                    <p className="blog-card__meta">
                      {formatDate(post.date)}
                      {post.author ? ` · ${post.author}` : ""}
                    </p>
                    <h2 className="blog-card__title">
                      <Link className="blog-card__titleLink" to={`/blog/${post.slug}`}>
                        {post.title}
                      </Link>
                    </h2>
                    {post.summary && <p className="blog-card__summary">{post.summary}</p>}
                    {Array.isArray(post.tags) && post.tags.length > 0 && (
                      <div className="blog-tag-list" aria-label="Post tags">
                        {post.tags.slice(0, 3).map((tag) => (
                          <span className="blog-tag-chip" key={`${post.slug}-${tag}`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </article>
                ))}
              </div>

              {getVisiblePosts(posts, activeTag).length === 0 && (
                <p>No posts match this tag yet.</p>
              )}
            </>
          )}
        </div>
      </section>
    </main>
  );
}

function getRankedTags(posts) {
  const counts = new Map();
  posts.forEach((post) => {
    (post.tags || []).forEach((tag) => {
      counts.set(tag, (counts.get(tag) || 0) + 1);
    });
  });

  return [...counts.entries()]
    .sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return a[0].localeCompare(b[0]);
    })
    .map(([tag]) => tag);
}

function getVisiblePosts(posts, activeTag) {
  if (!activeTag) return posts;
  return posts.filter((post) => Array.isArray(post.tags) && post.tags.includes(activeTag));
}

function formatDate(dateString) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}
